/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  fetchEducationByEmployeeId,
  createEducationBackground,
  updateEducationBackground,
  deleteEducationBackground,
  checkEmployeeCompleteness,
  uploadEducationFile,
} from "../../store/slices/educationBackgroundSlice";
import { fetchEmployeeById } from "../../store/slices/employeeDetailSlice";
import "bootstrap-icons/font/bootstrap-icons.css";

export const EducationLevel = {
  ELEMENTARY: "Elementary",
  JUNIORHIGH: "Junior High School",
  SENIORHIGH: "Senior High School",
  VOCATIONAL: "Vocational Course",
  COLLEGE: "College",
  POSTGRAD: "Post Graduate",
  OTHER: "Other",
};

const levelOrder = [
  EducationLevel.ELEMENTARY,
  EducationLevel.JUNIORHIGH,
  EducationLevel.SENIORHIGH,
  EducationLevel.VOCATIONAL,
  EducationLevel.COLLEGE,
  EducationLevel.POSTGRAD,
  EducationLevel.OTHER,
];

const EmployeeEducationBackgroundTable = () => {
  const dispatch = useDispatch();
  const { id: employeeId } = useParams();
  const { list = [] } = useSelector((state) => state.employeeEducation);
  const { employee } = useSelector((state) => state.employeeDetail);

  const [editableList, setEditableList] = useState([]);
  const [newLevel, setNewLevel] = useState(EducationLevel.ELEMENTARY);
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (employeeId) dispatch(fetchEducationByEmployeeId(employeeId));
  }, [employeeId, dispatch]);

  useEffect(() => {
    setEditableList(
      list
        .map((e) => ({ ...e, editing: false }))
        .sort((a, b) => levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level))
    );
  }, [list]);

  const showStatus = (text, type = "success") => {
    setStatusMsg({ text, type });
    setTimeout(() => setStatusMsg({ text: "", type: "" }), 4000);
  };

  const toggleEdit = (index) =>
    setEditableList((prev) =>
      prev.map((e, i) => (i === index ? { ...e, editing: !e.editing } : e))
    );

  const handleChange = (index, field, value) =>
    setEditableList((prev) =>
      prev.map((e, i) => (i === index ? { ...e, [field]: value } : e))
    );

  const handleCancel = (index, row) => {
    if (!row.id) {
      setEditableList(prev => prev.filter((_, i) => i !== index));
    } else {
      const original = list.find(e => e.id === row.id);
      setEditableList(prev => prev.map((e, i) => i === index ? { ...original, editing: false } : e));
    }
  };

  const handleSave = async (entry, index) => {
    if (!entry.schoolName?.trim()) {
      showStatus("School name is required.", "danger");
      return;
    }

    const companyId = employee?.companyId || employee?.company?.id;
    setIsProcessing(true);

    try {
      const payload = {
        employeeId,
        level: entry.level,
        schoolName: entry.schoolName,
        schoolAddress: entry.schoolAddress || null,
        yearFrom: Number(entry.yearFrom),
        yearTo: Number(entry.yearTo),
        course: entry.course || null,
      };

      let savedId = entry.id;
      if (entry.id) {
        await dispatch(updateEducationBackground({ id: entry.id, data: payload })).unwrap();
      } else {
        const res = await dispatch(createEducationBackground(payload)).unwrap();
        savedId = res.id;
      }

      if (entry.file && typeof entry.file !== "string") {
        await dispatch(uploadEducationFile({ 
          id: savedId, 
          file: entry.file, 
          companyId: companyId 
        })).unwrap();
      }

      showStatus("Saved successfully!");
      await dispatch(checkEmployeeCompleteness(employeeId)).unwrap();
      dispatch(fetchEducationByEmployeeId(employeeId));
      dispatch(fetchEmployeeById(employeeId));
    } catch (err) {
      showStatus(err?.message || "Failed to save.", "danger");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (entry, index) => {
    if (!entry.id) {
      setEditableList(prev => prev.filter((_, i) => i !== index));
      return;
    }
    if (!window.confirm("Delete record?")) return;

    setIsProcessing(true);
    try {
      await dispatch(deleteEducationBackground(entry.id)).unwrap();
      await dispatch(checkEmployeeCompleteness(employeeId)).unwrap();
      dispatch(fetchEducationByEmployeeId(employeeId));
      showStatus("Deleted successfully!");
    } catch (err) {
      showStatus("Failed to delete.", "danger");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddRow = () => {
    // Unique key generation for the new row to prevent cloning issues
    setEditableList((prev) => [
      ...prev,
      { id: null, level: newLevel, schoolName: "", schoolAddress: "", yearFrom: "", yearTo: "", course: "", editing: true, file: null },
    ]);
  };

  const headerStyle = { backgroundColor: "#1e293b", color: "#fff", fontSize: "0.7rem", fontWeight: "700", border: "none" };
  const inputStyle = { height: "32px", border: "1px solid rgba(0,0,0,0.1)", borderRadius: "8px", fontSize: "0.8rem" };

  return (
    <div className="container-fluid py-2 px-0">
      <div className="bg-white p-3 rounded-3 shadow-sm border mb-4 position-relative">
        {isProcessing && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="spinner-border text-primary"></div>
          </div>
        )}

        <div className="d-flex justify-content-end align-items-center px-3 py-2 rounded-2 mb-3" style={{ backgroundColor: "#1e293b" }}>
          <div className="d-flex align-items-center gap-3">
            {statusMsg.text && <span className={`fw-bold text-uppercase text-${statusMsg.type}`} style={{ fontSize: '0.65rem' }}>{statusMsg.text}</span>}
            <select className="form-select form-select-sm border-0" style={{ borderRadius: "6px", height: "28px", fontSize: "0.75rem", width: "auto" }} value={newLevel} onChange={(e) => setNewLevel(e.target.value)}>
              {levelOrder.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <button className="btn btn-sm px-3 text-white border-0" style={{ backgroundColor: "#198754", fontSize: '0.75rem', height: '28px' }} onClick={handleAddRow}>
              <i className="bi bi-plus-circle me-2"></i> Add
            </button>
          </div>
        </div>

        <div className="table-responsive rounded-2 border shadow-sm">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th className="py-3 ps-4" style={headerStyle}>LEVEL</th>
                <th className="py-3" style={headerStyle}>SCHOOL NAME</th>
                <th className="py-3 text-center" style={{ ...headerStyle, width: "80px" }}>FROM</th>
                <th className="py-3 text-center" style={{ ...headerStyle, width: "80px" }}>TO</th>
                <th className="py-3" style={headerStyle}>COURSE</th>
                <th className="py-3 text-center" style={headerStyle}>ATTACHMENT</th>
                <th className="py-3 pe-4 text-end" style={{ ...headerStyle, width: "130px" }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {editableList.map((row, index) => {
                const hasFile = row.file || row.file_url;
                
                return (
                  <tr key={row.id || `edu-${index}-${row.level}`} className={row.editing ? "bg-primary bg-opacity-10" : ""}>
                    <td className="ps-4 fw-bold text-primary text-uppercase" style={{ fontSize: '0.7rem' }}>{row.level}</td>
                    <td>
                      <input type="text" className={`form-control ${!row.editing ? 'bg-transparent border-0' : ''}`} style={row.editing ? inputStyle : { fontWeight: 600 }} value={row.schoolName} disabled={!row.editing} onChange={(e) => handleChange(index, "schoolName", e.target.value)} />
                      {row.editing && <input type="text" className="form-control mt-1" style={{ ...inputStyle, height: '24px', fontSize: '0.7rem' }} value={row.schoolAddress || ""} placeholder="Address" onChange={(e) => handleChange(index, "schoolAddress", e.target.value)} />}
                    </td>
                    <td className="text-center">
                      <input type="text" className={`form-control text-center ${!row.editing ? 'bg-transparent border-0' : ''}`} style={row.editing ? inputStyle : {}} value={row.yearFrom || ""} disabled={!row.editing} onChange={(e) => handleChange(index, "yearFrom", e.target.value.replace(/\D/g, ""))} />
                    </td>
                    <td className="text-center">
                      <input type="text" className={`form-control text-center ${!row.editing ? 'bg-transparent border-0' : ''}`} style={row.editing ? inputStyle : {}} value={row.yearTo || ""} disabled={!row.editing} onChange={(e) => handleChange(index, "yearTo", e.target.value.replace(/\D/g, ""))} />
                    </td>
                    <td>
                      {(row.level === EducationLevel.COLLEGE || row.level === EducationLevel.OTHER) ? (
                        <input type="text" className={`form-control ${!row.editing ? 'bg-transparent border-0' : ''}`} style={row.editing ? inputStyle : {}} value={row.course || ""} disabled={!row.editing} onChange={(e) => handleChange(index, "course", e.target.value)} />
                      ) : <span className="text-muted small px-2">—</span>}
                    </td>
                    <td className="text-center">
                      <div className="position-relative d-inline-block">
                        {row.editing && (
                          <input type="file" className="position-absolute opacity-0 w-100 h-100 cursor-pointer" style={{ zIndex: 2, top: 0, left: 0 }} 
                            onChange={(e) => {
                                handleChange(index, "file", e.target.files[0]);
                                e.target.value = ""; 
                            }} 
                          />
                        )}
                        <div className="d-flex align-items-center justify-content-center px-2 py-1 fw-bold text-white shadow-sm" 
                             style={{ 
                               fontSize: "0.6rem", 
                               backgroundColor: hasFile ? "#198754" : "#475569", 
                               borderRadius: "6px", minWidth: "90px", height: "26px" 
                             }}>
                          <i className={`bi ${hasFile ? "bi-check-circle-fill" : "bi-cloud-arrow-up-fill"} me-2`}></i>
                          {hasFile ? "UPLOADED" : "ATTACH"}
                        </div>
                      </div>
                    </td>
                    <td className="pe-4 text-end">
                      <div className="d-flex justify-content-end gap-2">
                        {row.editing ? (
                          <>
                            <button className="btn btn-success btn-sm p-0 d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }} onClick={() => handleSave(row, index)}><i className="bi bi-check-lg"></i></button>
                            <button className="btn btn-outline-secondary btn-sm p-0 d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }} onClick={() => handleCancel(index, row)}><i className="bi bi-x-lg"></i></button>
                          </>
                        ) : (
                          <>
                            <button className="btn btn-sm btn-outline-primary p-0 d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }} onClick={() => toggleEdit(index)}><i className="bi bi-pencil-square"></i></button>
                            {row.file_url && (
                              <a href={row.file_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary p-0 d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }}><i className="bi bi-eye-fill"></i></a>
                            )}
                            <button className="btn btn-sm btn-outline-danger p-0 d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }} onClick={() => handleDelete(row, index)}><i className="bi bi-trash"></i></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EmployeeEducationBackgroundTable;